/**
 * @fileoverview Componente atom para mostrar un enlace de ubicación con ícono, optimizado para InboxMC.
 * @module LocationMC
 * @category Component - Atom
 * @since 1.0.0
 * @version 1.1.0 - JSDoc añadido, optimización de props
 */
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import IconMC from "../../../components/IconMC";

/**
 * @typedef {Object} LocationMCProps
 * @property {string} title - Título o texto del enlace de ubicación. Si no se provee, se usa la traducción por defecto.
 * @property {string} locationURL - URL de la ubicación a mostrar/enlazar.
 * @property {string} [className] - Clases CSS adicionales para personalización.
 * @property {string} [target] - Destino del enlace (por defecto '_blank').
 * @property {string} [iconName] - Nombre del ícono a mostrar (por defecto 'pin-outline').
 * @property {string} [iconSize] - Tamaño del ícono (por defecto 'base').
 */

/**
 * Componente atom para mostrar un enlace de ubicación con ícono.
 *
 * Implementa el patrón Atom de Atomic Design, permitiendo reutilización y personalización.
 *
 * @component LocationMC
 * @category Component - Atom
 * @param {LocationMCProps} props - Propiedades del componente LocationMC
 * @returns {JSX.Element} Elemento JSX renderizado con enlace de ubicación
 *
 * @example
 * ```jsx
 * <LocationMC title="Ver ubicación" locationURL="https://maps.google.com/?q=19.4326,-99.1332" />
 * ```
 *
 * @see {@link IconMC} Componente de ícono utilizado
 */
// LocationMC - Componente puro para mostrar un enlace de ubicación
const LocationMC = memo(({ title, locationURL, className, target = "_blank", iconName = "pin-outline", iconSize = "base" }) => {
  const { t } = useTranslation('InboxMC');
  return (
    <a
      href={locationURL}
      target={target}
      rel="noopener noreferrer"
      className={`tw-flex tw-items-center hover:tw-underline tw-text-sm tw-font-normal ${className}`}
    >
      <IconMC size={iconSize} name={iconName} />
      <span className="ml-2">{title || t('location.view_location')}</span>
    </a>
  );
});

LocationMC.displayName = "LocationMC";

export default LocationMC;